import Meta from 'components/Meta'
import { Header } from 'components/marketing/Header'
import { TheGrid } from 'components/marketing/v2/TheGrid'
import { TheGradient } from 'components/marketing/v2/TheGradient'
import { FeatureAnnouncementBanner } from 'components/marketing/FeatureAnnouncementBanner'
import { HeroWithSideContent } from 'components/marketing/v2/HeroWithSideContent'
import { TrustedByCompanies } from 'components/marketing/v2/TrustedByCompanies'
import { TestimonialCard } from 'components/marketing/v2/TestimonialCard'
import { BentoCard } from 'components/marketing/v2/BentoCard'
import { WaitingFor } from 'components/marketing/v2/WaitingFor'
import HashnodeLogo from 'icons/HashnodeLogo'

function ImproveStability() {
  return (
    <div className='relative overflow-hidden'>
      <Meta
        title='Improve Stability with Stellate'
        description={`Deliver consistent performance, a reliable user experience, and service stability. With edge caching, rate limiting, and insights, your GraphQL APIs’ performance worries will be a thing of the past.`}
      />

      <FeatureAnnouncementBanner />

      <TheGrid />

      <Header />

      <main>
        <HeroWithSideContent
          eyeBrow={
            <span className='text-12 font-demibold text-blue bg-blue-15 rounded px-1.5 py-1'>
              Improve Stability
            </span>
          }
          headline='This is what a reliable GraphQL API looks like'
          subheadline='Deliver consistent performance, a reliable user experience, and service stability. With edge caching, rate limiting, and insights, your GraphQL APIs’ performance worries will be a thing of the past.'
          sideContent={
            <div className='mx-auto w-fit-content lg:ml-12'>
              <img
                src='/features/hero-improve-stability.svg'
                // eslint-disable-next-line react/no-unknown-property
                fetchPriority='high'
                width={771}
                height={467}
                className='max-w-none'
              />
            </div>
          }
        />

        <section className='relative'>
          <TheGradient />
          <div className='absolute inset-x-0 bottom-0 h-16 bg-gray-50' />

          <div className='max-w-7xl px-6 mx-auto relative'>
            <TrustedByCompanies />

            <div className='max-w-2xl mx-auto'>
              <TestimonialCard
                quote='We needed caching for our API, we wanted to make sure our database didn’t go down, and we needed to be as performant as possible. That’s when we found Stellate.'
                authorName='Alessandro Volpicella'
                authorTitle='Platform Engineering Lead'
                authorAvatarUrl='https://framerusercontent.com/images/nXtohDFy8Zn9chqTiFTp5EbKnBQ.jpg'
                companyLogo={
                  <HashnodeLogo className='h-8 w-16 object-contain fill-current' />
                }
                color='white'
                size='large'
                caseStudyLink='/case-study/hashnode-boosted-seo-and-stability'
              />
            </div>
          </div>
        </section>

        <section className='max-w-7xl px-6 mx-auto'>
          <div className='py-20 lg:py-36'>
            <div className='text-center space-y-2'>
              <div className='text-14 uppercase font-demibold tracking-wider'>
                <span className='bg-clip-text text-transparent bg-gradient-colorful-text'>
                  A look under the hood
                </span>
              </div>
              <h2 className='text-36 leading-1.2 font-bold text-neutral-110 lg:text-40'>
                Stellate&apos;s Mechanics
              </h2>
              <p className='text-20 text-balance lg:text-24'>
                Get rock-solid reliability, so you can sit back, relax, and
                watch as your GraphQL API runs flawlessly.
              </p>
            </div>
            <div className='mt-20 grid gap-9 max-w-lg mx-auto lg:max-w-none lg:grid-cols-3'>
              <div className='space-y-9'>
                <BentoCard
                  imgSrc='/features/bento-caching.svg'
                  headline='Edge Caching'
                  description='Significantly reduce the load on your origin servers leading to improved stability by caching GraphQL responses at the edge. Stellate minimizes repeated requests to your backend, resulting in faster response times and reduced strain on your infrastructure.'
                />
                <BentoCard
                  imgSrc='/features/bento-insights.svg'
                  headline='Monitoring & Insights'
                  description='Identify potential bottlenecks and areas for optimization with metrics and analytics that offer insights into your GraphQL API usage patterns, enabling better monitoring and stability enhancements.'
                />
              </div>
              <div className='space-y-9'>
                <BentoCard
                  imgSrc='/features/bento-pqc.svg'
                  headline='Partial Query Caching'
                  description='Precisely control which parts of your GraphQL responses are cached, optimizing performance and stability.'
                />
                <BentoCard
                  imgSrc='/features/bento-scale.svg'
                  headline='Scalability and Performance'
                  description='Reduce the load on your backend servers, enabling cost-effective infrastructure scaling with consistent performance and availability.'
                />
              </div>
              <div className='space-y-9'>
                <BentoCard
                  imgSrc='/features/bento-rate-limiting.svg'
                  headline='Rate Limiting'
                  description='Allows you to set controls for your API traffic. Defining rate limits based on factors such as user roles, request types, or specific fields, prevents resource drain and ensures stability for your API.'
                />
                <BentoCard
                  imgSrc='/features/bento-config.svg'
                  headline='Cache Configuration'
                  description='Tailor the cache configuration to your requirements with customizable cache rules based on specific GraphQL queries, mutations, or fields.'
                />
              </div>
            </div>
          </div>
        </section>

        <WaitingFor />
      </main>
    </div>
  )
}

export default ImproveStability
